import { defineStore } from 'pinia'
import {
  AddonService,
  type IAddonService,
  type TAddonServiceScope,
} from '@team-milva/lab-models/resources'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { db } from '@/firebase/init'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import { useAppStore } from '@/stores/appStore'
import { useAccountStore } from '@/stores/accountStore'

type CreateAddonServiceParams = {
  name: string
  scope: TAddonServiceScope
  applicableAnalysisIDs?: string[]
}

type UpdateAddonServiceParams = {
  name?: string
  scope?: TAddonServiceScope
  applicableAnalysisIDs?: string[]
  activeState?: 'active' | 'inactive'
}

export const useAddonServiceStore = defineStore('addonService', {
  state: () => ({
    allAddonServices: [] as AddonService[],
    unsubscribeAllAddonServices: null as (() => void) | null,
  }),

  getters: {
    allAddonServicesLoaded(): boolean {
      return !!this.unsubscribeAllAddonServices
    },
  },

  actions: {
    getAddonServices(): Promise<AddonService[]> {
      if (this.allAddonServicesLoaded) {
        return Promise.resolve(this.allAddonServices)
      }

      const accountStore = useAccountStore()
      if (!accountStore.labAccount?.id) {
        return Promise.resolve([])
      }

      return new Promise((resolve) => {
        let resolved = false
        const { currentLocale } = useAppStore()

        const q = query(
          collection(db, 'Apps', '-lab-management-', 'AddonServices'),
          where('labAccountID', '==', accountStore.labAccount!.id)
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          this.allAddonServices = querySnapshot.docs
            .map((doc) => new AddonService(doc.data() as IAddonService))
            .sort((a, b) =>
              a.getName(currentLocale).localeCompare(b.getName(currentLocale))
            )
          this.unsubscribeAllAddonServices = unsubscribe

          if (!resolved) {
            resolved = true
            resolve(this.allAddonServices)
          }
        })
      })
    },

    async createAddonService(params: CreateAddonServiceParams): Promise<void> {
      const { currentLocale } = useAppStore()
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/addon-services',
        {
          path: { labAccountID },
          body: {
            name: { [currentLocale]: params.name },
            scope: params.scope,
            applicableAnalysisIDs:
              params.scope === 'per-analysis'
                ? params.applicableAnalysisIDs ?? []
                : [],
          },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async updateAddonService(
      addonServiceID: string,
      params: UpdateAddonServiceParams
    ): Promise<void> {
      const { currentLocale } = useAppStore()
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const body: {
        name?: Record<string, string>
        scope?: TAddonServiceScope
        applicableAnalysisIDs?: string[]
        activeState?: 'active' | 'inactive'
      } = {}

      if (params.name !== undefined) {
        body['name'] = { [currentLocale]: params.name }
      }
      if (params.scope !== undefined) {
        body['scope'] = params.scope
      }
      if (params.applicableAnalysisIDs !== undefined) {
        body['applicableAnalysisIDs'] = params.applicableAnalysisIDs
      }
      if (params.activeState !== undefined) {
        body['activeState'] = params.activeState
      }

      const result = await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/addon-services/{addonServiceID}',
        {
          path: { labAccountID, addonServiceID },
          body,
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async deactivateAddonService(addonServiceID: string): Promise<void> {
      await this.updateAddonService(addonServiceID, {
        activeState: 'inactive',
      })
    },

    async reactivateAddonService(addonServiceID: string): Promise<void> {
      await this.updateAddonService(addonServiceID, { activeState: 'active' })
    },

    resetStore(): void {
      this.unsubscribeAllAddonServices?.()
      this.allAddonServices = []
      this.unsubscribeAllAddonServices = null
    },
  },
})
